<!-- Runtime.svelte -->
<script>
    import Icon from "@iconify/svelte";
    import { t } from "../lib/i18n";
    import refreshIcon from "@iconify/icons-mdi/refresh";
    import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  
    export let apiBase = "";
    export let apiKey = "";
    export let authMode = false;
  
    const JSON_HEADERS = { Accept: "application/json", "Cache-Control": "no-store" };
  
    let lines = [];
    let loading = false;
    let err = "";
    let lastUpdated = "";
  
    // small controls (keep boring)
    let limit = 100;
    let paused = false;
  
    // optional light filters
    let stream = "all"; // all | log | stdout | stderr | logtail
    let q = "";
  
    const pill =
      "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300";
  
    const sectionCard = "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";
  
    function authHeaders() {
      return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
    }
  
    function fmtTime(ts) {
      if (!ts) return "";
      try {
        const d = new Date(ts);
        // keep it readable; UTC marker not needed (server is UTC anyway)
        return d.toLocaleTimeString([], { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
      } catch {
        return ts;
      }
    }
  
    function lineColor(s) {
      if (s === "stderr") return "text-rose-200";
      if (s === "stdout") return "text-sky-200";
      if (s === "logtail") return "text-amber-200";
      return "text-zinc-200";
    }
  
    function badgeColor(s) {
      if (s === "stderr") return "border-rose-400/30 bg-rose-500/10 text-rose-100";
      if (s === "stdout") return "border-sky-400/30 bg-sky-500/10 text-sky-100";
      if (s === "logtail") return "border-amber-400/30 bg-amber-500/10 text-amber-100";
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-100";
    }
  
    function applyFilters(xs) {
      let out = xs || [];
      if (stream !== "all") out = out.filter((x) => (x?.stream || "") === stream);
      const needle = (q || "").trim().toLowerCase();
      if (needle) {
        out = out.filter((x) => (x?.line || "").toLowerCase().includes(needle));
      }
      return out;
    }
  
    async function fetchTail() {
      if (paused) return;
      loading = lines.length === 0; // only show spinner on first load
      err = "";
  
      try {
        const res = await fetch(`${apiBase}/tail`, {
          method: "GET",
          cache: "no-store",
          headers: { ...JSON_HEADERS, ...authHeaders() }
        });
  
        if (res.status === 401) {
          // Keep simple; App handles global logout flows elsewhere.
          throw new Error("401 unauthorized");
        }
        if (!res.ok) throw new Error(`tail failed: ${res.status}`);
  
        const data = await res.json();
        const raw = Array.isArray(data?.lines) ? data.lines : [];
        // server should already be DESC; if not, we’ll still cope
        const sliced = raw.slice(0, limit);
        lines = applyFilters(sliced);
        lastUpdated = new Date().toLocaleTimeString([], { hour12: false });
      } catch (e) {
        err = e?.message || String(e);
      } finally {
        loading = false;
      }
    }
  
    let timer = null;
  
    function start() {
      stop();
      fetchTail();
      timer = setInterval(fetchTail, 1000);
    }
  
    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }
  
    // start/stop with component lifecycle
    import { onMount, onDestroy } from "svelte";
    onMount(() => {
      start();
    });
    onDestroy(() => {
      stop();
    });
  
    // restart polling when un-pausing
    $: if (!paused && timer == null) start();
    $: if (paused && timer != null) stop();
  
    // re-apply filters without refetch
    $: lines = applyFilters(lines);
  </script>
  
  <main class="w-full px-4 py-6">
    <section class="ui-card p-5 sm:p-6">
      <!-- Header -->
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <span class="inline-flex h-9 w-9 items-center justify-center rounded border border-emerald-400/50 bg-emerald-500/30 text-emerald-50">
            <Icon icon="mdi:monitor-dashboard" width="18" height="18" />
          </span>
  
          <div>
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{$t('runtime.title')}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">
              {$t('runtime.subtitle')}
            </p>
          </div>
        </div>
  
        <div class="flex flex-wrap items-center gap-2">
          <span class={pill}>{$t('runtime.limit')}: {limit}</span>
          <span class={pill}>{$t('runtime.updated')}: {lastUpdated || "—"}</span>
  
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] px-3 h-10 text-sm text-zinc-200"
            on:click={() => fetchTail()}
            title={$t('runtime.refresh_now')}
          >
            <Icon icon={refreshIcon} width="18" height="18" />
            {$t('common.refresh')}
          </button>
  
          <button
            type="button"
            class={`inline-flex items-center gap-2 rounded-xl border px-3 h-10 text-sm ${
              paused
                ? "border-rose-400/30 bg-rose-500/10 text-rose-100"
                : "border-white/10 bg-white/[0.035] hover:bg-white/[0.06] text-zinc-200"
            }`}
            on:click={() => (paused = !paused)}
            title={paused ? $t('common.resume_polling') : $t('common.pause_polling')}
          >
            {paused ? $t('runtime.paused') : $t('runtime.pause')}
          </button>
        </div>
      </div>
  
      <!-- Controls -->
      <div class="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div class={sectionCard}>
          <div class="text-xs text-zinc-400">{$t('runtime.stream')}</div>
          <select
            class="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-3 h-10 text-sm text-zinc-200"
            bind:value={stream}
          >
            <option value="all">all</option>
            <option value="log">log</option>
            <option value="stdout">stdout</option>
            <option value="stderr">stderr</option>
            <option value="logtail">logtail</option>
          </select>
          <div class="mt-2 text-[11px] text-zinc-500">{$t('runtime.optional_filter')}</div>
        </div>
  
        <div class={sectionCard}>
          <div class="text-xs text-zinc-400">{$t('runtime.search')}</div>
          <input
            class="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-3 h-10 text-sm text-zinc-200 placeholder:text-zinc-600"
            placeholder={$t('runtime.search_placeholder')}
            bind:value={q}
            on:input={() => fetchTail()}
          />
          <div class="mt-2 text-[11px] text-zinc-500">{$t('runtime.case_insensitive')}</div>
        </div>
  
        <div class={sectionCard}>
          <div class="text-xs text-zinc-400">{$t('runtime.limit')}</div>
          <input
            type="number"
            min="10"
            max="100"
            step="10"
            class="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-3 h-10 text-sm text-zinc-200"
            bind:value={limit}
            on:change={() => fetchTail()}
          />
          <div class="mt-2 text-[11px] text-zinc-500">{$t('runtime.client_cap')}</div>
        </div>
      </div>
  
      <!-- Errors -->
      {#if err}
        <div class="mt-4 flex items-start gap-2 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          <Icon icon={alertIcon} width="18" height="18" class="mt-[2px]" />
          <div class="min-w-0">
            <div class="font-semibold">{$t('runtime.tail_error')}</div>
            <div class="mt-1 text-xs text-rose-200/90 break-words">{err}</div>
          </div>
        </div>
      {/if}
  
      <!-- Log panel -->
      <div class="mt-5 rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div class="text-sm font-semibold text-zinc-200">{$t('runtime.last_lines', { limit })}</div>
          <div class="text-xs text-zinc-500">{loading ? $t('common.loading') : $t('runtime.shown', { count: lines.length })}</div>
        </div>
  
        <div class="max-h-[68vh] overflow-auto">
          {#if loading && !lines.length}
            <div class="p-4 text-sm text-zinc-400">{$t('common.loading')}</div>
          {:else if !lines.length}
            <div class="p-4 text-sm text-zinc-500">{$t('runtime.no_lines')}</div>
          {:else}
            <div class="divide-y divide-white/5">
              {#each lines as it (it.time_utc + it.stream + it.line)}
                <div class="px-4 py-2.5 font-mono text-[12px] leading-snug">
                  <div class="flex items-start gap-3">
                    <div class="shrink-0 w-[86px] text-zinc-500" title={it.time_utc}>
                      {fmtTime(it.time_utc)}
                    </div>
  
                    <span class={`shrink-0 inline-flex items-center justify-center rounded-md border px-1.5 py-0.5 text-[10px] ${badgeColor(it.stream)}`}>
                      {it.stream}
                    </span>
  
                    <div class={`min-w-0 flex-1 break-words ${lineColor(it.stream)}`}>
                      {it.line}
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </section>
  </main>
  